import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Building2,
  Handshake,
  User,
  Star,
  Clock,
  Sparkles,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import Seo from "@/components/Seo";
import FinalCTA from "@/sections/FinalCTA";
import { SITE } from "@/lib/site";
import { recommendEntity, ENTITIES } from "@/lib/entity-content";

const ENTITY_ICON = { "pvt-ltd": Building2, llp: Handshake, opc: User };

const Q1 = { id: "fundraising", label: "Are you planning to raise VC or Angel funding?" };
const Q2 = { id: "founders", label: "How many founders or partners are in the business?" };
const Q3 = { id: "priority", label: "What is your main priority?" };

const OPTS_Q1 = [
  { v: "yes", label: "Yes" },
  { v: "no", label: "No / not sure yet" },
];
const OPTS_Q2 = [
  { v: "1", label: "1 (Solo founder)" },
  { v: "2+", label: "2 or more" },
];
const OPTS_Q3 = [
  { v: "low-compliance", label: "Low compliance & running cost" },
  { v: "investor-ready", label: "Fast investor readiness" },
  { v: "solo-ownership", label: "Solo ownership & flexibility" },
];

export default function EntityAdvisorPage() {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({ fundraising: "", founders: "", priority: "" });
  const [result, setResult] = useState(null);

  const set = (k) => (v) => setAnswers((s) => ({ ...s, [k]: v }));

  const compute = () => {
    const r = recommendEntity(answers);
    setResult(r);
  };

  const reset = () => {
    setAnswers({ fundraising: "", founders: "", priority: "" });
    setResult(null);
    setStep(1);
  };

  const canProceed =
    (step === 1 && answers.fundraising) ||
    (step === 2 && answers.founders) ||
    (step === 3 && answers.priority);

  const next = () => {
    if (step < 3) setStep(step + 1);
    else compute();
  };

  const back = () => {
    if (result) {
      setResult(null);
      return;
    }
    if (step > 1) setStep(step - 1);
  };

  return (
    <div data-testid="page-entity-advisor">
      <Seo
        title="Business Structure Advisor: Pvt Ltd, LLP or OPC?"
        description="Free 60-second tool for Indian founders. Answer 3 questions and get a founder-led recommendation on whether Pvt Ltd, LLP or OPC fits your business."
        path="/entity-advisor"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Entity Advisor", path: "/entity-advisor" },
        ]}
      />

      {/* Hero */}
      <section className="bg-white border-b border-[#EAEAEA]">
        <div className="max-w-[1024px] mx-auto px-5 md:px-8 py-14 md:py-20 text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#4A4A4A]">
              <Sparkles size={14} className="text-[#FDD805]" /> Business Structure Advisor
            </div>
            <h1 className="mt-4 font-display text-[38px] md:text-[56px] leading-[1.02] font-black tracking-tight text-[#0F1115]">
              Pvt Ltd, LLP or OPC? <br className="hidden md:block" />
              Find out in <span className="hl-yellow">60 seconds</span>.
            </h1>
            <p className="mt-5 text-[16px] md:text-[18px] text-[#1C1F26] max-w-2xl mx-auto leading-relaxed">
              A 3-question decision tool built by Bhavik Hariyani for First-Time Founders across Mumbai, Navi Mumbai, Thane and the MMRDA region. No sign-up. No pitches. Just clarity before you register.
            </p>
            <div className="mt-6 inline-flex items-center gap-4 text-[13px] text-[#4A4A4A]">
              <div className="inline-flex items-center gap-1.5">
                <Star size={13} fill="#FDD805" strokeWidth={0} className="text-[#FDD805]" />
                <span className="font-bold text-[#0F1115]">{SITE.googleRating}</span>
                <span>on {SITE.googleReviewCount} Google Reviews</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Wizard */}
      <section className="bg-[#F8F9FA]">
        <div className="max-w-[820px] mx-auto px-5 md:px-8 py-14 md:py-20">
          {!result && (
            <Reveal>
              <div className="bg-white border border-[#EAEAEA] rounded-3xl p-6 md:p-10 shadow-[0_10px_40px_rgba(0,0,0,0.04)]" data-testid="advisor-wizard">
                {/* Progress */}
                <div className="flex items-center gap-2" aria-label="Progress">
                  {[1, 2, 3].map((s) => (
                    <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-[#FDD805]" : "bg-[#EAEAEA]"}`} />
                  ))}
                </div>
                <div className="mt-3 text-[11px] uppercase tracking-[0.22em] font-bold text-[#4A4A4A]" data-testid="advisor-step-indicator">
                  Step {step} of 3
                </div>

                {step === 1 && (
                  <StepBlock
                    testId="advisor-step-1"
                    question={Q1.label}
                    options={OPTS_Q1}
                    value={answers.fundraising}
                    onChange={set("fundraising")}
                  />
                )}
                {step === 2 && (
                  <StepBlock
                    testId="advisor-step-2"
                    question={Q2.label}
                    options={OPTS_Q2}
                    value={answers.founders}
                    onChange={set("founders")}
                  />
                )}
                {step === 3 && (
                  <StepBlock
                    testId="advisor-step-3"
                    question={Q3.label}
                    options={OPTS_Q3}
                    value={answers.priority}
                    onChange={set("priority")}
                  />
                )}

                <div className="mt-10 flex items-center justify-between">
                  <button
                    onClick={back}
                    disabled={step === 1}
                    className="inline-flex items-center gap-1.5 text-[14px] font-bold text-[#0F1115] hover:opacity-70 transition-opacity disabled:opacity-30"
                    data-testid="advisor-back"
                  >
                    <ArrowLeft size={16} /> Back
                  </button>
                  <button
                    onClick={next}
                    disabled={!canProceed}
                    className="bg-[#0F1115] text-white rounded-full px-7 py-3 font-bold text-[14px] hover:bg-[#1c1f26] transition-colors disabled:opacity-40 inline-flex items-center gap-2"
                    data-testid="advisor-next"
                  >
                    {step === 3 ? "See recommendation" : "Next"} <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </Reveal>
          )}

          {result && <ResultCard result={result} onReset={reset} answers={answers} />}
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}

const StepBlock = ({ question, options, value, onChange, testId }) => (
  <div className="mt-8" data-testid={testId}>
    <h2 className="font-display text-[22px] md:text-[28px] font-black leading-tight text-[#0F1115]">
      {question}
    </h2>
    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
      {options.map((o) => {
        const selected = value === o.v;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            className={`text-left p-5 rounded-2xl border-2 transition-all ${selected
                ? "border-[#0F1115] bg-[#FEF6C7] shadow-[0_6px_20px_rgba(0,0,0,0.04)]"
                : "border-[#EAEAEA] bg-white hover:border-[#0F1115]/40"
              }`}
            data-testid={`advisor-option-${o.v}`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected ? "border-[#0F1115] bg-[#0F1115]" : "border-[#4A4A4A]"}`}>
                {selected && <div className="w-2 h-2 rounded-full bg-[#FDD805]" />}
              </div>
              <div className="font-display font-bold text-[15px] text-[#0F1115]">
                {o.label}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

const ResultCard = ({ result, onReset, answers }) => {
  const Entity = ENTITY_ICON[result.primary.slug] || Building2;
  const params = new URLSearchParams({
    text:
      "Hi Bhavik, the entity advisor recommended " +
      result.primary.longName +
      " for me. Can we get on a call?",
  });
  const waLink = `https://wa.me/${SITE.whatsappNumber}?${params.toString()}`;

  return (
    <div data-testid="advisor-result">
      <Reveal>
        <div className="bg-white border border-[#EAEAEA] rounded-3xl overflow-hidden shadow-[0_16px_60px_rgba(0,0,0,0.06)]">
          <div className="bg-[#FDD805] px-8 py-5 flex items-center justify-between flex-wrap gap-3">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#0F1115]">
              Recommended for you
            </div>
            <button
              onClick={onReset}
              className="text-[12px] uppercase tracking-[0.18em] font-bold text-[#0F1115] hover:underline"
              data-testid="advisor-restart"
            >
              Restart
            </button>
          </div>

          <div className="p-8 md:p-12">
            <div className="flex items-start gap-5">
              <div className="w-16 h-16 rounded-2xl bg-[#FEF6C7] flex items-center justify-center shrink-0">
                <Entity size={28} className="text-[#0F1115]" />
              </div>
              <div>
                <div className="font-display font-black text-[32px] md:text-[42px] leading-tight text-[#0F1115]">
                  {result.primary.longName}
                </div>
                <div className="mt-1 text-[15px] text-[#4A4A4A]">
                  {result.primary.tagline}
                </div>
              </div>
            </div>

            <div className="mt-6 p-5 bg-[#F8F9FA] border-l-4 border-[#FDD805] rounded-r-xl text-[15.5px] leading-[1.65] text-[#0F1115]">
              {result.reason}
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#4A4A4A]">
                  Ideal for
                </div>
                <ul className="mt-3 space-y-2">
                  {result.primary.idealFor.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-[14.5px]">
                      <CheckCircle2 size={16} className="text-[#FDD805] mt-0.5 shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#4A4A4A]">
                  Estimated timeline
                </div>
                <div className="mt-3 inline-flex items-center gap-2 bg-[#0F1115] text-white rounded-2xl px-5 py-4">
                  <Clock size={18} className="text-[#FDD805]" />
                  <div className="font-display font-black text-[22px] leading-none">
                    {result.primary.timeline}
                  </div>
                </div>
                <div className="mt-3 text-[12.5px] text-[#4A4A4A]">
                  Subject to document readiness and MCA processing.
                </div>
              </div>
            </div>

            {result.fallback && (
              <div className="mt-6 p-4 bg-[#FFF9E6] border border-[#FDD805]/50 rounded-xl text-[13.5px] text-[#0F1115]">
                <b>Alternative to consider:</b> {result.fallback.longName} \u2014 {result.fallback.tagline}
              </div>
            )}

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer noopener"
                className="bg-[#25D366] text-white rounded-full px-7 py-4 font-bold text-[15px] inline-flex items-center justify-center gap-2 hover:bg-[#1EBE57] transition-colors"
                data-testid="advisor-whatsapp-cta"
              >
                <WhatsAppIcon size={18} /> Speak directly with Founder Bhavik
              </a>
              <Link
                to={result.primary.urlPath}
                className="bg-white text-[#0F1115] border-2 border-[#0F1115] rounded-full px-7 py-3.5 font-bold text-[15px] inline-flex items-center justify-center gap-2 hover:bg-[#0F1115] hover:text-white transition-colors"
                data-testid="advisor-learn-more"
              >
                Learn more about {result.primary.shortName} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </Reveal>

      <div className="mt-6 text-center">
        <button
          onClick={onReset}
          className="text-[13px] font-bold text-[#4A4A4A] hover:text-[#0F1115] link-underline"
        >
          Answer again with different inputs
        </button>
      </div>
    </div>
  );
};
